/**
 * Shared CSV reading. Every import flow has to agree on this: a cell that one
 * flow trims and another does not stops matching the same record, and a file
 * one flow decodes as utf-8 and another as windows-1252 stops matching at all.
 */

import Papa from "papaparse";

import { m } from "@/paraglide/messages";

export const CSV_DELIMITERS = [",", ";", "\t", "|", "^", "~"];

export type CsvEncoding = "utf-8" | "utf-16le" | "utf-16be" | "windows-1252";

/** the file is not text in any encoding we read, so decoding it would only invent characters */
export class CsvEncodingError extends Error {
  constructor() {
    super("The CSV file is not encoded in UTF-8, UTF-16 or Windows-1252.");
    this.name = "CsvEncodingError";
  }
}

const BOMS = [
  { bytes: [0xef, 0xbb, 0xbf], encoding: "utf-8" },
  { bytes: [0xff, 0xfe], encoding: "utf-16le" },
  { bytes: [0xfe, 0xff], encoding: "utf-16be" },
] as const;

const startsWith = (bytes: Uint8Array, prefix: readonly number[]) =>
  prefix.every((byte, i) => bytes[i] === byte);

/** windows-1252 codepoints for bytes 0x80-0x9f, the range where it differs from latin-1 */
const CP1252_HIGH_CONTROLS =
  "\u20ac\u0081\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u008d\u017d\u008f\u0090\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u009d\u017e\u0178";

/**
 * Node decodes 0x80-0x9f as latin-1 pass-through where browsers apply the
 * windows-1252 table, so the euro sign and curly quotes Excel writes are text
 * in the app but control characters under test. Remapping that range is a
 * no-op wherever the decoder already got it right.
 */
const applyCp1252HighControls = (text: string) =>
  text.replaceAll(/[\u0080-\u009f]/g, (char) =>
    CP1252_HIGH_CONTROLS.charAt((char.codePointAt(0) ?? 0) - 0x80),
  );

/**
 * Excel writes accented letters as single bytes unless the user picks "CSV UTF-8",
 * so a strict utf-8 read is the test: valid means utf-8, invalid means the
 * windows-1252 those bytes almost always are. A nul byte means neither: it is
 * bom-less utf-16, which decodes to interleaved nuls rather than failing.
 */
const decodeBytes = (
  bytes: Uint8Array,
): { text: string; encoding: CsvEncoding } => {
  const bom = BOMS.find((candidate) => startsWith(bytes, candidate.bytes));
  if (bom) {
    return {
      text: new TextDecoder(bom.encoding).decode(
        bytes.subarray(bom.bytes.length),
      ),
      encoding: bom.encoding,
    };
  }

  // checked ahead of the utf-8 attempt because nul is itself valid utf-8
  if (bytes.includes(0x00)) throw new CsvEncodingError();

  try {
    return {
      text: new TextDecoder("utf-8", { fatal: true }).decode(bytes),
      encoding: "utf-8",
    };
  } catch {
    return {
      text: applyCp1252HighControls(
        new TextDecoder("windows-1252").decode(bytes),
      ),
      encoding: "windows-1252",
    };
  }
};

/**
 * What a failed read tells the user, in one place so every import flow says the
 * same thing. The error goes along for the ride as the toast dedupe key.
 */
export const csvErrorToast = (error: unknown) => {
  if (error instanceof CsvEncodingError) {
    return {
      title: m.toast_csv_unsupported_encoding(),
      description: m.toast_csv_unsupported_encoding_description(),
      severity: "danger" as const,
      error,
    };
  }
  return {
    title: m.toast_failed_to_parse_csv_file(),
    description: error instanceof Error ? error.message : undefined,
    severity: "danger" as const,
    error,
  };
};

/**
 * Text form matters as much as encoding: macOS exports "e" plus a combining
 * acute where the server sends a single "e-acute", and those two never compare equal.
 */
export const readCsvText = async (
  file: File,
): Promise<{ text: string; encoding: CsvEncoding }> => {
  const { text, encoding } = decodeBytes(
    new Uint8Array(await file.arrayBuffer()),
  );
  return { text: text.normalize("NFC"), encoding };
};

/** the one parse every import flow shares, so a file read by two of them yields the same rows */
export const parseCsvFile = async (file: File): Promise<string[][]> => {
  const { text } = await readCsvText(file);
  // papaparse reports problems on the result rather than throwing, and the ones
  // it can report here are all recoverable: a one-column file always reports an
  // undetectable delimiter and still parses, and a row whose field count is off
  // is padded or truncated later by sanitizeDataRow, which every flow relies on
  // for ragged exports. Failing the import on either would reject files that
  // import correctly today, so the collected errors are deliberately dropped.
  return Papa.parse<string[]>(text, {
    delimiter: "",
    delimitersToGuess: CSV_DELIMITERS,
    header: false,
    skipEmptyLines: "greedy",
    dynamicTyping: false,
  }).data;
};

const stripBOM = (value: string) => value.replace(/^\uFEFF/, "");

export const sanitizeCell = (value?: string | null): string | null => {
  if (value === undefined || value === null) return null;
  const cleaned = stripBOM(value).replaceAll("\r", "").replaceAll("\xA0", " ");
  const trimmed = cleaned.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") return null;
  return trimmed;
};

export const sanitizeHeaderRow = (row: string[]): string[] =>
  row.map((cell) => sanitizeCell(cell) ?? "");

export const sanitizeDataRow = (
  row: string[],
  targetLength: number,
): (string | null)[] => {
  const sanitized = row.map((cell) => sanitizeCell(cell));
  if (sanitized.length < targetLength) {
    return [
      ...sanitized,
      ...Array.from({ length: targetLength - sanitized.length }, () => null),
    ];
  }
  if (sanitized.length > targetLength) {
    return sanitized.slice(0, targetLength);
  }
  return sanitized;
};

/** A cell's text as the file would show it; anything not primitive has no textual form worth matching on. */
export const cellText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};
